import {
   Anchor,
   Badge,
   Button,
   Container,
   Divider,
   Grid,
   Group,
   Image,
   Modal,
   Paper,
   ScrollArea,
   Stack,
   Table,
   Text,
   ThemeIcon,
   Title,
} from "@mantine/core";
import { motion } from "framer-motion";
import {
   IconArrowLeft,
   IconCamera,
   IconCheck,
   IconCurrencyNaira,
   IconEye,
   IconFileDescription,
   IconHeartHandshake,
   IconIdBadge,
   IconMapPin,
   IconReportAnalytics,
   IconShield,
   IconUser,
   IconX,
   IconBuildingBank,
   IconDownload,
} from "@tabler/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Endpoints } from "../../lib/endpoints";
import { usePerformRequest, PerformRequest } from "../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../types";
import { IAdminUserDetailsResponse } from "../../types/Admin";
import { ILoanRequestResponseProps } from "../../types/Loan";

const loanStatusColors: Record<string, string> = {
   pending: "yellow",
   approved: "green",
   rejected: "red",
};

const userStatusColors: Record<string, string> = {
   ACTIVE: "green",
   SUSPENDED: "red",
   DEACTIVATED: "gray",
};

const formatDateTime = (timestamp?: number) => {
   if (!timestamp) return "N/A";
   return new Date(timestamp).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
   });
};

const formatCurrency = (value?: number) => {
   if (value === undefined || value === null) return "N/A";
   return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
   }).format(value);
};

const InfoRow = ({ label, value }: { label: string; value?: ReactNode }) => (
   <Group justify="space-between" align="flex-start">
      <Text size="sm" fw={600} c="dimmed">
         {label}
      </Text>
      <Text size="sm" fw={500} ta="right" style={{ maxWidth: "60%" }}>
         {value ?? "N/A"}
      </Text>
   </Group>
);

const KycStatusBadge = ({ status }: { status?: string }) => {
   if (!status) return <Badge color="gray">Not Submitted</Badge>;
   const map: Record<string, string> = {
      awaiting: "gray",
      pending: "yellow",
      approved: "green",
      rejected: "red",
   };

   return (
      <Badge color={map[status] || "gray"} size="sm">
         {status.toUpperCase()}
      </Badge>
   );
};

export default function UserDetails() {
   const { userId } = useParams<{ userId: string }>();
   const navigate = useNavigate();

   const {
      data: userDetails,
      isLoading,
      reloadData,
   } = usePerformRequest<NonPaginatedResponse<IAdminUserDetailsResponse>>({
      method: "GET",
      route: userId ? Endpoints.GetSingleUser(userId).route : "",
   });
   const profile = userDetails?.data;
   const user = profile?.user;
   const kyc = profile?.kyc;
   const loanStats = profile?.loanStats;
   const loanRequests = profile?.loanRequests ?? [];

   const renderVerificationBadge = (label: string, value?: boolean) => (
      <Badge color={value ? "green" : "red"} size="sm">
         {label}: {value ? "Yes" : "No"}
      </Badge>
   );

   const loanStatCards = useMemo(
      () => [
         { label: "Total Loan Requests", value: loanStats?.total ?? 0, color: "#1E3A8A" },
         { label: "Approved", value: loanStats?.approved ?? 0, color: "#15803D" },
         { label: "Pending", value: loanStats?.pending ?? 0, color: "#B45309" },
         { label: "Rejected", value: loanStats?.rejected ?? 0, color: "#B91C1C" },
      ],
      [loanStats]
   );

   const kycDocumentBlocks = useMemo(
      () => [
         {
            title: "Identity Document",
            status: kyc?.identityStatus,
            doc: kyc?.identity,
            icon: <IconIdBadge size={20} />,
         },
         {
            title: "Proof of Employment",
            status: kyc?.employmentStatus,
            doc: kyc?.proofOfEmployment,
            icon: <IconFileDescription size={20} />,
         },
         {
            title: "Proof of Address",
            status: kyc?.addressStatus,
            doc: kyc?.proofOfAddress,
            icon: <IconMapPin size={20} />,
         },
         {
            title: "Financial Document",
            status: kyc?.financialDocumentStatus,
            doc: kyc?.financialDocument,
            icon: <IconReportAnalytics size={20} />,
         },
      ],
      [kyc]
   );

   const [waiveModalOpen, setWaiveModalOpen] = useState(false);
   const [isWaiving, setIsWaiving] = useState(false);
   const [isVerifyingMandate, setIsVerifyingMandate] = useState(false);
   const [isRetryingActivation, setIsRetryingActivation] = useState(false);

   const handleWaiveBvn = async () => {
      if (!userId || !user) return;

      try {
         setIsWaiving(true);
         const response = await PerformRequest<NonPaginatedResponse<IAdminUserDetailsResponse>>({
            method: Endpoints.WaiveUserBvn(userId).method,
            route: Endpoints.WaiveUserBvn(userId).route,
         });

         if (response && response.status === 200) {
            await reloadData();
            setWaiveModalOpen(false);
         } else {
            alert(response?.message || "Failed to waive BVN verification");
         }
      } catch (error: any) {
         alert(error?.message || "Failed to waive BVN verification");
      } finally {
         setIsWaiving(false);
      }
   };

   const handleVerifyMandate = async () => {
      if (!userId || !user) return;
      try {
         setIsVerifyingMandate(true);
         const response = await PerformRequest<NonPaginatedResponse<any>>({
            method: Endpoints.VerifyUserDirectDebitMandate(userId).method,
            route: Endpoints.VerifyUserDirectDebitMandate(userId).route,
            data: {
               reference: (user as any)?.directDebitMandate?.reference,
            },
         });

         if (response && response.status === 200) {
            await reloadData();
         } else {
            alert(response?.message || "Failed to verify direct debit mandate");
         }
      } catch (error: any) {
         alert(error?.message || "Failed to verify direct debit mandate");
      } finally {
         setIsVerifyingMandate(false);
      }
   };

   const handleRetryActivation = async () => {
      if (!userId || !user) return;
      try {
         setIsRetryingActivation(true);
         const response = await PerformRequest<NonPaginatedResponse<any>>({
            method: Endpoints.RetryUserDirectDebitActivation(userId).method,
            route: Endpoints.RetryUserDirectDebitActivation(userId).route,
         });

         if (response && response.status === 200) {
            await reloadData();
         } else {
            alert(response?.message || "Failed to retry direct debit activation");
         }
      } catch (error: any) {
         alert(error?.message || "Failed to retry direct debit activation");
      } finally {
         setIsRetryingActivation(false);
      }
   };

   if (isLoading) {
      return (
         <Container size="xl" py="xl">
            <Paper shadow="sm" p="xl" radius="sm" style={{ background: "white", border: "2px solid #e5e7eb" }}>
               <Stack align="center" gap="lg" py="xl">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#699269]"></div>
                  <Text size="lg" c="dimmed">
                     Loading user profile...
                  </Text>
               </Stack>
            </Paper>
         </Container>
      );
   }

   if (!user) {
      return (
         <Container size="xl" py="xl">
            <Paper shadow="sm" p="xl" radius="sm" style={{ background: "white", border: "2px solid #e5e7eb" }}>
               <Stack align="center" gap="lg" py="xl">
                  <Text size="xl" fw={700} c="red">
                     User not found
                  </Text>
                  <Button onClick={() => navigate("/dashboard/users")} leftSection={<IconArrowLeft size={18} />}>
                     Back to Users
                  </Button>
               </Stack>
            </Paper>
         </Container>
      );
   }

   const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";

   return (
      <Container size="xl" py="xl">
         <Modal
            opened={waiveModalOpen}
            onClose={() => (isWaiving ? null : setWaiveModalOpen(false))}
            centered
            title={
               <Group gap="xs">
                  <ThemeIcon size={32} radius="md" color="red">
                     <IconShield size={18} />
                  </ThemeIcon>
                  <Text fw={700} size="lg">
                     Waive BVN Verification
                  </Text>
               </Group>
            }
         >
            <Stack gap="sm">
               <Text size="sm" c="dimmed">
                  This will **manually mark BVN profile, phone number, and BVN selfie as verified** for this user,
                  bypassing automated checks.
               </Text>
               <Text size="sm" fw={500}>
                  Only proceed if you are absolutely sure this user has been verified through an alternate process.
               </Text>
               <Group justify="flex-end" mt="md">
                  <Button variant="subtle" onClick={() => setWaiveModalOpen(false)} disabled={isWaiving}>
                     Cancel
                  </Button>
                  <Button color="red" loading={isWaiving} onClick={handleWaiveBvn}>
                     Confirm Waiver
                  </Button>
               </Group>
            </Stack>
         </Modal>

         <Stack gap="xl">
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
               <Group justify="space-between" align="flex-start">
                  <Group align="center" gap="md">
                     <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={18} />}
                        onClick={() => navigate("/dashboard/users")}
                        style={{ color: "#679369" }}
                     >
                        Back to Users
                     </Button>
                     <div>
                        <Title order={1} size="2.5rem" fw={800} style={{ color: "#1F2937" }}>
                           {fullName}
                        </Title>
                        <Text size="lg" c="dimmed">
                           Comprehensive customer profile, verification, and lending history
                        </Text>
                     </div>
                  </Group>
                  <Group gap="sm">
                     <Badge color={userStatusColors[user.status] || "gray"} size="lg">
                        {user.status}
                     </Badge>
                     <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        loading={isVerifyingMandate}
                        onClick={handleVerifyMandate}
                     >
                        Verify Mandate
                     </Button>
                     <Button
                        size="xs"
                        variant="light"
                        color="green"
                        loading={isRetryingActivation}
                        onClick={handleRetryActivation}
                     >
                        Retry Activation
                     </Button>
                     <Button
                        size="xs"
                        variant="outline"
                        color="red"
                        onClick={() => setWaiveModalOpen(true)}
                        disabled={user.isBvnImageVerified === true}
                     >
                        Waive BVN Verification
                     </Button>
                  </Group>
               </Group>
            </motion.div>

            <Grid gutter="lg">
               <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                     <Stack gap="md">
                        <Group gap="xs">
                           <ThemeIcon size={42} radius="md" style={{ background: "#14532D" }}>
                              <IconUser size={22} color="white" />
                           </ThemeIcon>
                           <Title order={3} fw={700}>
                              Personal Information
                           </Title>
                        </Group>
                        <Divider />
                        <Stack gap="sm">
                           <InfoRow label="Full Name" value={fullName} />
                           <InfoRow
                              label="Email"
                              value={
                                 <Group gap="xs">
                                    <Text size="sm">{user.email}</Text>
                                    {user.emailVerified ? (
                                       <IconCheck size={16} color="#22C55E" />
                                    ) : (
                                       <IconX size={16} color="#EF4444" />
                                    )}
                                 </Group>
                              }
                           />
                           <InfoRow
                              label="Phone"
                              value={
                                 <Group gap="xs">
                                    <Text size="sm">{user.phone}</Text>
                                    {user.phoneVerified ? (
                                       <IconCheck size={16} color="#22C55E" />
                                    ) : (
                                       <IconX size={16} color="#EF4444" />
                                    )}
                                 </Group>
                              }
                           />
                           <InfoRow label="Gender" value={user.gender ? user.gender.toUpperCase() : "N/A"} />
                           <InfoRow
                              label="Marital Status"
                              value={user.maritalStatus ? user.maritalStatus.toUpperCase() : "N/A"}
                           />
                           <InfoRow label="Date of Birth" value={formatDateTime(user.dateOfBirth)} />
                           <InfoRow
                              label="Bank Verification Number"
                              value={user.bankVerificationNumber ?? "Not provided"}
                           />
                        </Stack>
                     </Stack>
                  </Paper>
               </Grid.Col>

               <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                     <Stack gap="md">
                        <Group gap="xs">
                           <ThemeIcon size={42} radius="md" style={{ background: "#1D4ED8" }}>
                              <IconShield size={22} color="white" />
                           </ThemeIcon>
                           <Title order={3} fw={700}>
                              Account & Access
                           </Title>
                        </Group>
                        <Divider />
                        <Stack gap="sm">
                           <InfoRow label="Account Created" value={formatDateTime(user.createdAt)} />
                           <InfoRow label="Last Login" value={formatDateTime(user.lastLoginAt)} />
                           <InfoRow label="Last Updated" value={formatDateTime(user.updatedAt)} />
                           <Group gap="xs">
                              {renderVerificationBadge("Email Verified", user.emailVerified)}
                              {renderVerificationBadge("Phone Verified", user.phoneVerified)}
                              {renderVerificationBadge("KYC Verified", user.kycVerified)}
                           </Group>
                           <Group gap="xs">
                              <Badge color={user.isBvnImageVerified ? "green" : "yellow"} size="sm">
                                 BVN Selfie {user.isBvnImageVerified ? "Verified" : "Pending"}
                              </Badge>
                              <Badge color={user.bvnInfo ? "green" : "gray"} size="sm">
                                 BVN Profile {user.bvnInfo ? "Synced" : "Missing"}
                              </Badge>
                              {user.paystackCustomerCode && (
                                 <Badge color="blue" size="sm">
                                    Paystack Customer
                                 </Badge>
                              )}
                              {user.bankAccount && (
                                 <Badge color="green" size="sm">
                                    Bank Account Set
                                 </Badge>
                              )}
                           </Group>
                        </Stack>
                     </Stack>
                  </Paper>
               </Grid.Col>

               {user.address && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                     <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                        <Stack gap="md">
                           <Group gap="xs">
                              <ThemeIcon size={42} radius="md" style={{ background: "#059669" }}>
                                 <IconMapPin size={22} color="white" />
                              </ThemeIcon>
                              <Title order={3} fw={700}>
                                 Address Information
                              </Title>
                           </Group>
                           <Divider />
                           <Stack gap="sm">
                              <InfoRow label="Address" value={user?.address?.address} />
                              <InfoRow label="Nearest Landmark" value={user?.address.nearestLandmark} />
                              <InfoRow label="Bus Stop" value={user?.address.busStop} />
                              <InfoRow label="City" value={user?.address.city} />
                              <InfoRow label="LGA" value={user?.address.localGovernmentArea} />
                              <InfoRow label="State" value={user?.address.state} />
                              <InfoRow label="Country" value={user?.address.country} />
                           </Stack>
                        </Stack>
                     </Paper>
                  </Grid.Col>
               )}

               {user.nextOfKin && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                     <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                        <Stack gap="md">
                           <Group gap="xs">
                              <ThemeIcon size={42} radius="md" style={{ background: "#C2410C" }}>
                                 <IconHeartHandshake size={22} color="white" />
                              </ThemeIcon>
                              <Title order={3} fw={700}>
                                 Next of Kin
                              </Title>
                           </Group>
                           <Divider />
                           <Stack gap="sm">
                              <InfoRow label="First Name" value={user.nextOfKin.firstName} />
                              <InfoRow label="Surname" value={user.nextOfKin.surname} />
                              <InfoRow label="Phone" value={user.nextOfKin.phoneNumber} />
                              <InfoRow label="Email" value={user.nextOfKin.emailAddress} />
                              <InfoRow label="Relationship" value={user.nextOfKin.relationship} />
                           </Stack>
                        </Stack>
                     </Paper>
                  </Grid.Col>
               )}
            </Grid>

            <Grid gutter="lg">
               <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                     <Stack gap="md">
                        <Group gap="xs">
                           <ThemeIcon size={42} radius="md" style={{ background: "#0F172A" }}>
                              <IconIdBadge size={22} color="white" />
                           </ThemeIcon>
                           <Title order={3} fw={700}>
                              BVN Verification Snapshot
                           </Title>
                        </Group>
                        <Divider />
                        {user.bvnInfo ? (
                           <Stack gap="sm">
                              <InfoRow label="BVN" value={user.bvnInfo.bvn ?? user.bankVerificationNumber} />
                              <InfoRow
                                 label="Full Name"
                                 value={
                                    `${user.bvnInfo.first_name || ""} ${user.bvnInfo.middle_name || ""} ${
                                       user.bvnInfo.last_name || ""
                                    }`.trim() || "N/A"
                                 }
                              />
                              <InfoRow
                                 label="Date of Birth"
                                 value={user.bvnInfo.formatted_dob || user.bvnInfo.dob || "N/A"}
                              />
                              <InfoRow label="Gender" value={user.bvnInfo.gender || "N/A"} />
                              <InfoRow label="Mobile" value={user.bvnInfo.mobile || "N/A"} />
                              <InfoRow label="Email" value={user.bvnInfo.email || "N/A"} />
                              <InfoRow label="State of Origin" value={user.bvnInfo.state_of_origin || "N/A"} />
                              <InfoRow label="LGA of Origin" value={user.bvnInfo.lga_of_origin || "N/A"} />
                              <InfoRow label="State of Residence" value={user.bvnInfo.state_of_residence || "N/A"} />
                              <InfoRow label="LGA of Residence" value={user.bvnInfo.lga_of_residence || "N/A"} />
                              <InfoRow label="Residential Address" value={user.bvnInfo.residential_address || "N/A"} />
                              <InfoRow label="Enrollment Bank" value={user.bvnInfo.enrollment_bank || "N/A"} />
                              <InfoRow label="Enrollment Branch" value={user.bvnInfo.enrollment_branch || "N/A"} />
                              <InfoRow label="Registration Date" value={user.bvnInfo.registration_date || "N/A"} />
                              {user.bvnInfo.watchlisted !== undefined && (
                                 <InfoRow
                                    label="Watchlisted"
                                    value={
                                       <Badge color={user.bvnInfo.watchlisted > 0 ? "red" : "green"}>
                                          {user.bvnInfo.watchlisted > 0 ? "Yes" : "No"}
                                       </Badge>
                                    }
                                 />
                              )}
                              {user.bvnInfo.base64Image && (
                                 <Anchor href={user.bvnInfo.base64Image} target="_blank" rel="noopener noreferrer">
                                    <Button variant="light" leftSection={<IconEye size={16} />}>
                                       View BVN Photo
                                    </Button>
                                 </Anchor>
                              )}
                           </Stack>
                        ) : (
                           <Text size="sm" c="dimmed">
                              BVN information has not been synced for this user.
                           </Text>
                        )}
                     </Stack>
                  </Paper>
               </Grid.Col>

               <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                     <Stack gap="md">
                        <Group gap="xs">
                           <ThemeIcon size={42} radius="md" style={{ background: "#7C3AED" }}>
                              <IconCamera size={22} color="white" />
                           </ThemeIcon>
                           <Title order={3} fw={700}>
                              BVN Selfie Verification
                           </Title>
                        </Group>
                        <Divider />
                        {user.bvnImage ? (
                           <Stack gap="sm">
                              <Image src={user.bvnImage} alt="BVN Selfie" radius="md" height={220} fit="cover" />
                              <Group gap="xs">
                                 <Badge color={user.isBvnImageVerified ? "green" : "yellow"}>
                                    {user.isBvnImageVerified ? "Verified" : "Pending Review"}
                                 </Badge>
                                 <Text size="sm" c="dimmed">
                                    Selfie stored for manual audit trail
                                 </Text>
                              </Group>
                           </Stack>
                        ) : (
                           <Stack gap="sm">
                              <Text size="sm" c="dimmed">
                                 No selfie has been captured for BVN match yet.
                              </Text>
                              <Badge color="yellow">Action Required</Badge>
                           </Stack>
                        )}
                     </Stack>
                  </Paper>
               </Grid.Col>
            </Grid>

            {(user.bankAccount || user.paystackCustomerCode || user.paystackVirtualAccount) && (
               <Grid gutter="lg">
                  {user.bankAccount && (
                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                           <Stack gap="md">
                              <Group gap="xs">
                                 <ThemeIcon size={42} radius="md" style={{ background: "#059669" }}>
                                    <IconCurrencyNaira size={22} color="white" />
                                 </ThemeIcon>
                                 <Title order={3} fw={700}>
                                    Bank Account
                                 </Title>
                              </Group>
                              <Divider />
                              <Stack gap="sm">
                                 <InfoRow label="Account Name" value={user.bankAccount.accountName} />
                                 <InfoRow label="Account Number" value={user.bankAccount.accountNumber} />
                                 <InfoRow label="Bank Name" value={user.bankAccount.bankName || "N/A"} />
                                 <InfoRow label="Bank Code" value={user.bankAccount.bankCode} />
                                 {user.bankAccount.bankId && (
                                    <InfoRow label="Bank ID" value={user.bankAccount.bankId.toString()} />
                                 )}
                              </Stack>
                           </Stack>
                        </Paper>
                     </Grid.Col>
                  )}
                  {user.paystackCustomerCode && (
                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                           <Stack gap="md">
                              <Group gap="xs">
                                 <ThemeIcon size={42} radius="md" style={{ background: "#7C3AED" }}>
                                    <IconShield size={22} color="white" />
                                 </ThemeIcon>
                                 <Title order={3} fw={700}>
                                    Paystack Integration
                                 </Title>
                              </Group>
                              <Divider />
                              <Stack gap="sm">
                                 <InfoRow label="Customer Code" value={user.paystackCustomerCode} />
                                 <Badge color="green" size="lg">
                                    Paystack Customer Profile Active
                                 </Badge>
                              </Stack>
                           </Stack>
                        </Paper>
                     </Grid.Col>
                  )}
                  {user.paystackVirtualAccount && (
                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                           <Stack gap="md">
                              <Group gap="xs">
                                 <ThemeIcon size={42} radius="md" style={{ background: "#0C4A6E" }}>
                                    <IconBuildingBank size={22} color="white" />
                                 </ThemeIcon>
                                 <Title order={3} fw={700}>
                                    Paystack Virtual Account
                                 </Title>
                              </Group>
                              <Divider />
                              <Stack gap="sm">
                                 <InfoRow label="Account Name" value={user.paystackVirtualAccount.accountName} />
                                 <InfoRow label="Account Number" value={user.paystackVirtualAccount.accountNumber} />
                                 <InfoRow label="Bank Name" value={user.paystackVirtualAccount.bankName || "N/A"} />
                                 <InfoRow label="Bank Code" value={user.paystackVirtualAccount.bankSlug || "N/A"} />
                                 <InfoRow label="Currency" value={user.paystackVirtualAccount.currency || "NGN"} />
                              </Stack>
                           </Stack>
                        </Paper>
                     </Grid.Col>
                  )}
               </Grid>
            )}

            <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
               <Stack gap="md">
                  <Group justify="space-between">
                     <Group gap="xs">
                        <ThemeIcon size={42} radius="md" style={{ background: "#DC2626" }}>
                           <IconShield size={22} color="white" />
                        </ThemeIcon>
                        <div>
                           <Title order={3} fw={700}>
                              KYC Documents
                           </Title>
                           <Text size="sm" c="dimmed">
                              Every file the user submitted for compliance review
                           </Text>
                        </div>
                     </Group>
                     <Button
                        variant="light"
                        onClick={() => navigate("/dashboard/kyc")}
                        leftSection={<IconEye size={16} />}
                     >
                        Open KYC Desk
                     </Button>
                  </Group>
                  <Divider />
                  <Grid gutter="md">
                     {kycDocumentBlocks.map((doc) => (
                        <Grid.Col key={doc.title} span={{ base: 12, md: 6 }}>
                           <Paper withBorder p="md" radius="md">
                              <Stack gap="xs">
                                 <Group justify="space-between">
                                    <Group gap="xs">
                                       <ThemeIcon radius="sm" variant="light" color="green">
                                          {doc.icon}
                                       </ThemeIcon>
                                       <div>
                                          <Text fw={600}>{doc.title}</Text>
                                          <Text size="xs" c="dimmed">
                                             {doc.doc?.documentType ?? "Not submitted"}
                                          </Text>
                                       </div>
                                    </Group>
                                    <KycStatusBadge status={doc.status} />
                                 </Group>
                                 {doc.doc?.file ? (
                                    <Group gap="xs">
                                       <Anchor
                                          href={doc.doc.file}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ flex: 1 }}
                                       >
                                          <Button
                                             fullWidth
                                             variant="light"
                                             color="green"
                                             leftSection={<IconFileDescription size={16} />}
                                          >
                                             View Document
                                          </Button>
                                       </Anchor>
                                       <Button
                                          variant="light"
                                          color="blue"
                                          leftSection={<IconDownload size={16} />}
                                          onClick={() => {
                                             const link = document.createElement("a");
                                             link.href = doc.doc.file;
                                             link.download = `${doc.title.replace(/\s+/g, "_")}_${
                                                doc.doc.documentType || "document"
                                             }.${doc.doc.file.split(".").pop()?.split("?")[0] || "pdf"}`;
                                             document.body.appendChild(link);
                                             link.click();
                                             document.body.removeChild(link);
                                          }}
                                       >
                                          Download
                                       </Button>
                                    </Group>
                                 ) : (
                                    <Text size="sm" c="dimmed">
                                       No document uploaded for this section.
                                    </Text>
                                 )}
                              </Stack>
                           </Paper>
                        </Grid.Col>
                     ))}
                  </Grid>
               </Stack>
            </Paper>

            <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
               <Stack gap="lg">
                  <Group justify="space-between" align="flex-start">
                     <Group gap="xs">
                        <ThemeIcon size={42} radius="md" style={{ background: "#0F766E" }}>
                           <IconReportAnalytics size={22} color="white" />
                        </ThemeIcon>
                        <div>
                           <Title order={3} fw={700}>
                              Loan Activity
                           </Title>
                           <Text size="sm" c="dimmed">
                              Full breakdown of borrowing behaviour and request history
                           </Text>
                        </div>
                     </Group>
                     <Group gap="sm">
                        {loanStatCards.map((card) => (
                           <Paper
                              key={card.label}
                              p="sm"
                              radius="md"
                              style={{ background: card.color, color: "white", minWidth: 150 }}
                           >
                              <Text size="xs" tt="uppercase" fw={600}>
                                 {card.label}
                              </Text>
                              <Text size="xl" fw={700}>
                                 {card.value}
                              </Text>
                           </Paper>
                        ))}
                     </Group>
                  </Group>
                  <Divider />
                  {loanRequests.length === 0 ? (
                     <Stack align="center" gap="xs">
                        <IconCurrencyNaira size={40} color="#9CA3AF" />
                        <Text c="dimmed">No loan requests yet for this user.</Text>
                     </Stack>
                  ) : (
                     <ScrollArea>
                        <Table striped highlightOnHover withTableBorder>
                           <Table.Thead>
                              <Table.Tr style={{ background: "#F9FAFB" }}>
                                 <Table.Th>Product</Table.Th>
                                 <Table.Th>Amount</Table.Th>
                                 <Table.Th>Duration (months)</Table.Th>
                                 <Table.Th>Purpose</Table.Th>
                                 <Table.Th>Status</Table.Th>
                                 <Table.Th>Requested</Table.Th>
                                 <Table.Th>Last Update</Table.Th>
                              </Table.Tr>
                           </Table.Thead>
                           <Table.Tbody>
                              {loanRequests.map((request: ILoanRequestResponseProps) => (
                                 <Table.Tr key={request.id}>
                                    <Table.Td>{request.loanID?.name ?? "Unknown product"}</Table.Td>
                                    <Table.Td>{formatCurrency(request.amount)}</Table.Td>
                                    <Table.Td>{request.duration}</Table.Td>
                                    <Table.Td>{request.purpose}</Table.Td>
                                    <Table.Td>
                                       <Badge color={loanStatusColors[request.status] || "gray"}>
                                          {request.status.toUpperCase()}
                                       </Badge>
                                    </Table.Td>
                                    <Table.Td>{formatDateTime(request.createdAt)}</Table.Td>
                                    <Table.Td>{formatDateTime(request.updatedAt)}</Table.Td>
                                 </Table.Tr>
                              ))}
                           </Table.Tbody>
                        </Table>
                     </ScrollArea>
                  )}
               </Stack>
            </Paper>
         </Stack>
      </Container>
   );
}
